import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import round from 'lodash/round';

import {OVERALL_PROP} from 'common/report/models/funnels/constants';
import {ColumnGroup, SortTransformFunc, TableColumn} from 'common/components/data-table/types';
import {renamePropertyOld} from 'common/report/util/properties';
import {abbreviateNumber4SigFigs, commaizeNumber} from 'common/util';
import {parseDateSortKey, parseNumberRangeSortKey} from 'common/util/header-sorting';
import {formatDuration, MS_BY_UNIT} from 'common/util/date';
import {
  BasicFunnelStep,
  FunnelSegment,
  FunnelStep,
  PValue,
  SelectedColumns,
  TableNonToggleableMetrics,
  TableToggleableMetrics,
} from 'common/report/models/funnels/types';
import {formatPercent} from 'common/report/util/funnels/utils';
import {getTableColumnSpec} from 'common/report/util/funnels/table-width';
import {PropertyType} from 'common/report/constants';

import {FunnelTableRow, StatSigDisplay, StatSigDisplayStatusColor} from './types';
import {getCellStatSigRenderer, SegmentName} from './cell-renderers';
import type {FunnelsTable} from './index';

function overallConversionRatio(step: BasicFunnelStep | FunnelStep): number {
  return round(step.overall_conv_ratio * 100, 2);
}

function stepConversionRatio(step: BasicFunnelStep | FunnelStep): number {
  return round(step.step_conv_ratio * 100, 2);
}

function countTransform(row: FunnelTableRow, field: string) {
  const value = row[field];
  return value >= 10 ** 7 ? `${abbreviateNumber4SigFigs(value)}` : `${commaizeNumber(value)}`;
}

function pctTransform(row: FunnelTableRow, field: string) {
  return formatPercent(row[field], 2);
}

function fixedPointPercentTransform(row: FunnelTableRow, field: string) {
  return formatPercent(row[field], 2, true, true);
}

export function formatStepConversionDuration(seconds) {
  if (!isNumber(seconds)) {
    return `-`;
  }
  const duration =
    seconds < 500 ? {value: round(seconds, 0), unit: `s`} : formatDuration(seconds * MS_BY_UNIT.second, 1, true);
  return `${duration.value}${duration.unit}`;
}

export function getStatisticalSignificance(pValue: PValue): StatSigDisplay {
  const {confidence, is_interesting: isInteresting, insufficient_samples: insufficientSamples} = pValue;
  if (insufficientSamples) {
    return {confidence: `-`};
  }
  return {
    confidence: confidence > 0.99 ? `0.99` : confidence.toFixed(2),
    status: isInteresting ? StatSigDisplayStatusColor.Green : StatSigDisplayStatusColor.Red,
    label: isInteresting ? `Statistically significant` : `Not statistically significant`,
  };
}

function stepStatsLabelTransform(row: FunnelTableRow, field: string): string {
  return formatStepConversionDuration(row[field]);
}

export function statSigSortTransform(row: FunnelTableRow): number {
  if (!row.statSig) {
    return null;
  }
  const {status} = row.statSig;
  const {count} = row.displayInfo.steps[0];
  const totalFunnelSteps = row.displayInfo.steps;
  const {conversionRatio} = row.displayInfo.steps[totalFunnelSteps.length - 1];
  if (status === StatSigDisplayStatusColor.Green) {
    return -1 * conversionRatio;
  } else if (status === StatSigDisplayStatusColor.Red) {
    return 1.0 / count;
  } else {
    return 1 + 1.0 / count;
  }
}

/**
 * Limit the helpers available to mixins/cellRenderers.
 *
 * This keep getColumnsGroups easy to test. Add more keys here
 * as needed.
 **/
type MixinHelpers = `setControl`;

const FIELD_STAT_SIG = `statSig`;
export const FIELD_HEADER = `header`;
export const DEFAULT_SORT_ORDER = 1;
export const DEFAULT_SORT_FIELD = FIELD_STAT_SIG;

export function getHeaderSortTransform(segmentType: PropertyType): SortTransformFunc {
  if (segmentType === PropertyType.Datetime) {
    return (row: FunnelTableRow, field) => {
      return parseDateSortKey(row[field]);
    };
  } else if (segmentType === PropertyType.Number) {
    return (row: FunnelTableRow, field, {sortOrder}) => {
      return parseNumberRangeSortKey(row[field], {sortOrder});
    };
  } else {
    return null;
  }
}

export function getColumnGroups({
  helpers,
  stepNames,
  segmentNames,
  segmentType,
  shouldShowStatSig,
  selectedColumns,
  tableWidth,
}: {
  helpers: Pick<FunnelsTable['helpers'], MixinHelpers>;
  stepNames: string[];
  segmentNames: string[];
  segmentType: PropertyType;
  shouldShowStatSig: boolean;
  selectedColumns: SelectedColumns;
  tableWidth: number;
}): ColumnGroup[] {
  let segmentNameCombined = ``;
  let isSegmented = false;
  if (segmentNames.length) {
    isSegmented = true;
    segmentNameCombined = segmentNames.map((name) => renamePropertyOld(name)).join(` / `);
  }

  const percentTransform = isSegmented ? fixedPointPercentTransform : pctTransform;
  const isSortable = isSegmented;

  const spec = getTableColumnSpec(
    selectedColumns,
    segmentNames.length,
    stepNames.length,
    shouldShowStatSig,
    tableWidth,
  );
  const groups: TableColumn[][] = spec.map((columnGroup) =>
    columnGroup.map((column, idx) => {
      const commonProps = {
        width: column.width,
        last: idx === columnGroup.length - 1,
      };

      switch (column.identifier) {
        case TableNonToggleableMetrics.Segment:
          if (isSegmented) {
            return {
              ...commonProps,
              field: FIELD_HEADER,
              title: `Property`,
              label: segmentNameCombined,
              sticky: true,
              isSortable: !!segmentNameCombined,
              cellRenderer: SegmentName,
              sortTransform: getHeaderSortTransform(segmentType),
            };
          } else {
            return {
              ...commonProps,
              field: `property`,
              title: `Property`,
              cellRenderer: SegmentName,
              sticky: true,
            };
          }
        case TableNonToggleableMetrics.OverallConv:
          return {
            ...commonProps,
            field: `overall`,
            type: `number`,
            isSortable: isSegmented,
            sticky: true,
            title: `Total Conv.`,
            tooltip: `Percent who converted all steps`,
            labelTransform: percentTransform,
            minWidth: column.width,
          };

        case TableToggleableMetrics.StatSig:
          return {
            ...commonProps,
            field: FIELD_STAT_SIG,
            type: `number`,
            cellRenderer: getCellStatSigRenderer(helpers),
            isSortable: true,
            sortTransform: statSigSortTransform,
            title: `Sig.`,
            tooltip: `Statistical significance`,
            italics: true,
          };
        case TableToggleableMetrics.Count:
          return {
            ...commonProps,
            field: `step-${column.stepIdx}`,
            type: `number`,
            tooltip: `Number who performed this step`,
            icon: `value-absolute`,
            isSortable,
            labelTransform: countTransform,
          };
        case TableToggleableMetrics.ConvPrevStep:
          return {
            ...commonProps,
            field: `step-${column.stepIdx}-pct`,
            type: `number`,
            tooltip: `Conversion from previous step`,
            icon: `conversion-prev-step`,
            isSortable,
            labelTransform: percentTransform,
            minWidth: column.width,
          };
        case TableToggleableMetrics.ConvFirstStep:
          return {
            ...commonProps,
            field: `step-${column.stepIdx}-overall-pct`,
            type: `number`,
            tooltip: `Conversion from first step`,
            icon: `conversion-first-step`,
            isSortable,
            labelTransform: percentTransform,
          };
        case TableToggleableMetrics.TimePrevStep:
          return {
            ...commonProps,
            field: `statistics-${column.stepIdx}`,
            type: `number`,
            tooltip: `Average time from previous step`,
            icon: `time-prev-step`,
            isSortable,
            labelTransform: stepStatsLabelTransform,
          };
        case TableToggleableMetrics.TimeFirstStep:
          return {
            ...commonProps,
            field: `statistics-overall-${column.stepIdx}`,
            type: `number`,
            tooltip: `Average time from first step`,
            icon: `time-first-step`,
            isSortable,
            labelTransform: stepStatsLabelTransform,
          };
        case TableNonToggleableMetrics.NoMetrics:
        default:
          return {
            ...commonProps,
            field: `unselected`,
            type: `number`,
            tooltip: `Add metrics to view them here`,
            title: `No metrics`,
            isSortable: false,
          };
      }
    }),
  );

  return groups.map(
    (columns): ColumnGroup => {
      return {columns};
    },
  );
}

function shouldShowPValues(segmentSteps: FunnelSegment[]): boolean {
  return !!segmentSteps && segmentSteps.length > 0;
}

// TODO opslead
// eslint-disable-next-line sonarjs/cognitive-complexity
export function getRows({
  selected,
  controlGroup,
  segmentSteps,
  shouldShowStatSig = true,
  shouldFilterForSelected = false,
}: {
  selected: Map<string, string>;
  segmentSteps: FunnelSegment[];
  controlGroup: string[];
  shouldShowStatSig?: boolean;
  shouldFilterForSelected?: boolean;
}): FunnelTableRow[] {
  if (!segmentSteps.length) {
    return [];
  }
  const displayPValues = shouldShowStatSig && shouldShowPValues(segmentSteps);

  const totalFunnelSteps = segmentSteps[0].steps.length;
  const segmentRows = segmentSteps.flatMap((segment) => {
    if (shouldFilterForSelected && !selected.has(segment.prop)) {
      return [];
    }
    const averageTimeBetweenSteps = [];
    const infoSteps = [];
    const isOverall = segment.prop === OVERALL_PROP;
    segment.steps.forEach((step) => {
      averageTimeBetweenSteps.push(step.avg_time);
      infoSteps.push({
        count: step.count,
        conversionRatio: isOverall ? overallConversionRatio(step) : stepConversionRatio(step),
      });
    });

    const prop = segment.prop;

    const isControlGroup = controlGroup?.length
      ? isEqual(
          controlGroup,
          segment.props.map((p) => p.prop),
        )
      : isOverall;

    const segmentDict: FunnelTableRow = {
      // TODO: Duplicate of prop in FunnelTableRow. Dedupe
      identifier: prop,
      header: segment.propName,
      prop,
      props: segment.props,
      overall: segment.steps[segment.steps.length - 1].overall_conv_ratio,
      isSelected: selected.has(segment.prop),
      isControlGroup,
      displayInfo: {
        steps: infoSteps,
        averageTimeBetweenSteps,
      },
    };

    if (segmentDict.isSelected) {
      segmentDict.checkedColor = selected.get(segment.prop);
    }

    segment.steps.forEach((step, index) => {
      segmentDict[`step-${index}`] = step.count;
      segmentDict[`step-${index}-pct`] = step.step_conv_ratio;
      segmentDict[`step-${index}-overall-pct`] = step.overall_conv_ratio;
      segmentDict[`statistics-overall-${index}`] = step.avg_time_from_start;
      segmentDict[`statistics-${index}`] = segment.steps[index].avg_time;
    });

    if (isOverall) {
      segmentDict.statSig = {confidence: ``};
    } else if (displayPValues && !isControlGroup) {
      const lastStep = segment.steps[totalFunnelSteps - 1];

      // @ts-ignore - TODO: Property 'p_value' does not exist on type 'BasicFunnelStep | FunnelStep'
      segmentDict.statSig = getStatisticalSignificance(lastStep.p_value);
    }
    return segmentDict;
  });

  // Move Overall row to first position
  const overallIndex = segmentRows.findIndex((row) => row.prop === OVERALL_PROP);
  if (overallIndex !== -1) {
    const overallRow = segmentRows.splice(overallIndex, 1)[0];
    segmentRows.splice(0, 0, overallRow);
  }

  // Move control group row to be the first position after Overall
  const controlGroupIndex = segmentRows.findIndex((row) => row.isControlGroup);
  const hasOverall = overallIndex !== -1;
  if (controlGroupIndex !== -1 && controlGroupIndex !== 0) {
    const controlGroupRow = segmentRows.splice(controlGroupIndex, 1)[0];
    segmentRows.splice(hasOverall ? 1 : 0, 0, controlGroupRow);
  }

  return segmentRows;
}
